import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil,map,tap, switchMap, catchError} from 'rxjs/operators';
import { ActivationData, ActivationStatusStep, ActivationStep, SmartCardData } from '../activation.model';
import { ActivationService } from '../services/activation.service';
import { BissManager } from '../services/biss-manager';
import { AngularDeviceInformationService } from 'angular-device-information';

@Component({
  selector: 'cloud-pin-puk',
  templateUrl: './pin-puk.component.html',
  styleUrls: ['./pin-puk.component.scss']
})
export class PinPukComponent implements OnInit {

  @Output() stepCompleteEvent = new EventEmitter();

  @Input()
  activationData: ActivationData;
  smartCardData: SmartCardData;

  pinValue: string = "PIN: "
  pukValue: string = "PUK: "

  pinChanged: boolean = false;
  pukChanged: boolean = false;

  oldPin: string;
  oldPuk: string;

  bissSerial: string;
  isOsLinux: boolean;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public info: InfoService,
    private translateService: TranslateService,
    private activationService: ActivationService,
    private bissManager: BissManager,
    private deviceInfoService: AngularDeviceInformationService) {
  }

  ngOnInit() {
    this.getSmartCardData();
    this.checkOperatingSystem();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  checkOperatingSystem() {
    const deviceInfo = this.deviceInfoService.getDeviceInfo();
    this.isOsLinux = deviceInfo.os.toLowerCase() === 'linux';
  }

  getSmartCardData() {
    return this.activationService.getSmartCardData(this.activationData.serviceRequestId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.smartCardData = res.scData;
        if (this.smartCardData.originalPin && this.smartCardData.originalPuk) {
          this.oldPin = this.smartCardData.originalPin.substring(0, 4);
          this.oldPuk = this.smartCardData.originalPuk.substring(0, 10);
        } else {
          this.oldPin = '1234';
          this.oldPuk = '1111111111';
        }
        this.pinValue += this.oldPin;
        this.pukValue += this.oldPuk;
      });
  }

  getOldPuk() { }

  changePin() {
    if (this.bissSerial) {
      this.bissManager.changePIN(this.smartCardData.csp, this.bissSerial)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if(res !== null) {
          this.pinChanged = true;
          this.updateStatus(this.activationData.serviceRequestId);
          this.info.success(this.translateService.instant('activation.pin.pinChangeSuccessMessage'));
        }else {
          this.info.error(this.translateService.instant('activation.pin.notChangedMessage'))
        }
      });
    } else {
      this.bissManager.checkSmartCard()
      .pipe(
          switchMap((observable:Observable<any>) => {
            return observable.pipe(switchMap(r =>{
              this.bissSerial = r.cardSerialNumber
              return this.bissManager.changePIN(this.smartCardData.csp, this.bissSerial) 
            }))
          }),
          takeUntil(this.unsubscribe$))
          .subscribe((res) => {
            if(res !== null) {
              this.pinChanged = true;
              this.updateStatus(this.activationData.serviceRequestId);
              this.info.success(this.translateService.instant('activation.pin.pinChangeSuccessMessage'));
          }else{
            this.info.error(this.translateService.instant('activation.pin.notChangedMessage'))
          }
        });
    }
  }

  changePuk() {
    if (this.bissSerial) {
      this.bissManager.changePUK(this.smartCardData.csp, this.bissSerial)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if(res !== null) {
          this.pukChanged = true;
          this.updateStatus(this.activationData.serviceRequestId);
          this.info.success(this.translateService.instant('activation.pin.pukChangeSuccessMessage'));
        }else{
          this.info.error(this.translateService.instant('activation.pin.notChangedMessage'))
        }
      });
    } else {
      this.bissManager.checkSmartCard()
        .pipe(
          switchMap((observable: Observable<any>) => {
            return observable.pipe(switchMap(r => {
              this.bissSerial = r.cardSerialNumber
              return this.bissManager.changePUK(this.smartCardData.csp, this.bissSerial)
            }))
          }),
          takeUntil(this.unsubscribe$))
          .subscribe((res) => {
            if(res !== null) {
              this.pukChanged = true;
              this.updateStatus(this.activationData.serviceRequestId);
              this.info.success(this.translateService.instant('activation.pin.pukChangeSuccessMessage'));
            }else{
              this.info.error(this.translateService.instant('activation.pin.notChangedMessage'))
            }
          });
    }
  }

  updateStatus(serviceRequestId: string) {
    if (this.pinChanged === true && this.pukChanged === true){
      this.activationService.updateStatus(serviceRequestId, ActivationStatusStep.SUCCESSFULLY_CHANGE_PIN_PUK)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe();
    }
  }

  isPinPukChanged(): boolean {
    return this.pinChanged && this.pukChanged;
  }

  stepComplete() {
    this.stepCompleteEvent.emit(ActivationStep.PIN);
  }
}
